import { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import { type KycFormData, CardholderType, getDefaultDocumentType, getAllowedCardholderTypes } from '../types'

interface StepSelectCardholderTypeProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  onNext: () => void
  onBack: () => void
  /** 开卡产品下发的 KYC 类型白名单；空/未传不限制 */
  allowedKycTypes?: string[]
}

const getCardholderTypeOptions = (t: (key: string) => string) => [
  {
    type: CardholderType.Mainland,
    title: t('大陆持卡人'),
    desc: t('当前仅支持身份证验证'),
    icon: '/icon/mainland.svg',
  },
  {
    type: CardholderType.Overseas,
    title: t('海外持卡人'),
    desc: t('当前仅支持护照验证'),
    icon: '/icon/overseas.svg',
  },
]

export default function StepSelectCardholderType({ formData, updateFormData, onNext, onBack, allowedKycTypes }: StepSelectCardholderTypeProps) {
  const { t } = useTranslation()
  const options = getCardholderTypeOptions(t)
  const [selectedType, setSelectedType] = useState<CardholderType>(formData.cardholder_type)
  const { mainland: mainlandAllowed, overseas: overseasAllowed, unrestricted } = getAllowedCardholderTypes(allowedKycTypes)
  const canProceedSelectType = unrestricted || mainlandAllowed || overseasAllowed

  useEffect(() => {
    setSelectedType(formData.cardholder_type)
  }, [formData.cardholder_type])

  const isTypeEnabled = useCallback((type: CardholderType) => {
    if (type === CardholderType.Mainland) return mainlandAllowed
    return overseasAllowed
  }, [mainlandAllowed, overseasAllowed])

  const handleSelect = useCallback((type: CardholderType) => {
    if (!isTypeEnabled(type)) return
    setSelectedType(type)
    const defaultDocType = getDefaultDocumentType(type)
    updateFormData({
      cardholder_type: type,
      identity_type: defaultDocType,
      identity_number: '',
      idCardFrontUrl: '',
      idCardBackUrl: '',
      idCardHandheldUrl: '',
      passportFrontUrl: '',
      passportHandheldUrl: '',
      kyc_verified: false,
    })
  }, [updateFormData, isTypeEnabled])

  const handleNext = useCallback(() => {
    onNext()
  }, [onNext])

  return (
    <div className="flex flex-col h-full">
      <Header
        leftChildren={<Back onClick={onBack} />}
      />

      <div className="flex-1 px-5 pt-4 pb-24">
        <div className="text-7 font-medium text-[#1D2129] mb-2">
          {t('选择持卡人身份')}
        </div>

        <div className="text-sm text-[#9BA0AF] mb-8">
          {t('选择大陆/海外持卡人，完成认证')}
        </div>

        <div className="flex flex-col gap-4">
          {options.map((option) => {
            const isSelected = selectedType === option.type
            const enabled = isTypeEnabled(option.type)
            return (
              <div
                key={option.type}
                onClick={() => handleSelect(option.type)}
                className={`
                  flex items-center gap-4 px-3 py-5 rounded-4 border-1 border-solid transition-all
                  ${enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60 bg-[#F9F9F9]'}
                  ${isSelected && enabled
                    ? 'border-[var(--primary-color)]'
                    : 'border-[#EAECF0]'
                  }
                `}
              >
                <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-full ${enabled ? 'bg-[#F9FAFB]' : 'bg-[#F2F4F7]'}`}>
                  <img src={option.icon} className={`w-6 h-6 ${!enabled ? 'opacity-60' : ''}`} />
                </div>
                <div className="flex-1">
                  <div className={`text-base font-medium ${enabled ? 'text-[#061237]' : 'text-[#86909C]'}`}>
                    {option.title}
                  </div>
                  <div className={`text-xs mt-1 ${enabled ? 'text-[#9BA0AF]' : 'text-[#C9CDD4]'}`}>
                    {option.desc}
                  </div>
                </div>
                {isSelected && enabled && (
                  <div className="w-6 h-6 rounded-full bg-[var(--primary-color)] flex items-center justify-center flex-shrink-0">
                    <img src="/images/my/icons/select-kyc-type.svg" alt="" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <FixedBottom className="bg-white">
        <CustomButton onClick={handleNext} disabled={!canProceedSelectType}>
          {t('下一步')}
        </CustomButton>
      </FixedBottom>
    </div>
  )
}
